import { useEffect, useState } from 'react'
import { useAppSelector } from 'store/hooks'
import { DebouncedState, useDebouncedCallback } from 'use-debounce'

import { useLazyCheckDealerCodeQuery } from '../redux/orders.api'

type DealerCodeComponentProps = {
  debounceDealerChecking: DebouncedState<() => void>
  dealerDataLoading: boolean
  errorText: string | null
  setErrorText: (value: string | null) => void
  dealerName?: string
  onResetDealer: () => void
}

type UseDealerCodeCheckResult = {
  isDealerValid: boolean
  dealerCodeProps: DealerCodeComponentProps
}

export function useDealerCodeCheck(): UseDealerCodeCheckResult {
  const [errorText, setErrorText] = useState<string | null>(null)
  const [isDealerReset, setIsDealerReset] = useState(false)

  const currentEditForm = useAppSelector(
    state => state.orderForm.currentEditForm,
  )
  const dealerCode = useAppSelector(
    state => state.orderForm.forms[currentEditForm].dealerCode,
  )

  const [
    checkDealerCode,
    { data: dealerData, isFetching, isUninitialized, originalArgs },
  ] = useLazyCheckDealerCodeQuery()

  const debounceDealerChecking = useDebouncedCallback(() => {
    checkDealerCode(dealerCode)
  }, 300)

  useEffect(() => {
    if (!isFetching && originalArgs === dealerCode) {
      setIsDealerReset(false)
    }
  }, [isFetching, originalArgs, dealerCode])

  useEffect(() => {
    if (isUninitialized || dealerData === undefined || isFetching) return
    if (originalArgs !== dealerCode) return
    if (
      (dealerData && !!Object.keys(dealerData).length) ||
      dealerCode.length === 0
    ) {
      setErrorText(null)
      return
    }
    setErrorText('Дилер не найден')
  }, [dealerData, isFetching, originalArgs, dealerCode])

  const isDataCurrent =
    !isFetching && !isDealerReset && originalArgs === dealerCode
  const isDealerValid = Boolean(
    (isDataCurrent && dealerData && Object.keys(dealerData).length) ||
      dealerCode.length === 0,
  )

  return {
    isDealerValid,
    dealerCodeProps: {
      debounceDealerChecking,
      dealerDataLoading: isFetching,
      errorText,
      setErrorText,
      dealerName:
        isDataCurrent && !isUninitialized ? dealerData?.name : undefined,
      onResetDealer: () => setIsDealerReset(true),
    },
  }
}
